import { useIsMounted } from '@react-hookz/web';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { useRef, useState } from 'react';

import { STDMapSchema } from '@/api';
import { usePatchStdMapId, useStdMapId } from '@/api/queries/std';
import { Button, ButtonProps } from '@/components/Button/Button';
import { Divider } from '@/components/Divider/Divider';
import { useOpenModal } from '@/components/Modal/ModalManager';
import { TextEditor } from '@/components/TextEditor';
import { SaveFailed } from '@/modals/SaveFailed';
import { useUserStore } from '@/stores/user';
import { useElementSize } from '@/utils/hooks/useElementSize';

import { VoorschriftenLayout } from './-components/voorschriftenLayout';

/**
 * Route
 */
export const Route = createFileRoute(
  '/beheer/standaardteksten/$grondslagId/$mapId/$sectieId/inhoudelijke-overwegingen'
)({
  beforeLoad: ({ context, params }) => ({
    getTitle: () => {
      const sectie = context.queryClient.getQueryData<STDMapSchema>(
        useStdMapId.getKey(params.sectieId)
      );

      if (!sectie) return;

      return `${sectie?.Naam} (inhoudelijke overwegingen)`;
    },
  }),
  loader: async ({ context: { queryClient }, params: { sectieId, mapId } }) => {
    const [sectie, map] = await Promise.all([
      queryClient.ensureQueryData(useStdMapId.getOptions(sectieId)),
      queryClient.ensureQueryData(useStdMapId.getOptions(mapId)),
    ]);

    if (!sectie || !map) throw notFound();
  },
  component: InhoudelijkeOverwegingenComponent,
});

/**
 * Route component
 */
function InhoudelijkeOverwegingenComponent() {
  const [containerRef, { height: containerHeight }] = useElementSize();

  const openModal = useOpenModal();

  const [content, setContent] = useState<string | null>();
  const [buttonState, setButtonState] = useState<
    ButtonProps['state'] | undefined
  >();

  const successTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const { mapId, sectieId, grondslagId } = Route.useParams();

  const map = useStdMapId(mapId);
  const sectie = useStdMapId(sectieId);
  const patchStdMapId = usePatchStdMapId();

  const isMounted = useIsMounted();

  if (!sectie.data || !map.data) return null;

  const handleUpdate = (value: string) => {
    setContent(value);
  };

  const handleSubmit = () => {
    const userId = useUserStore.getState().user?.ID ?? '';

    clearTimeout(successTimeoutRef.current);

    setButtonState('loading');

    patchStdMapId.mutate(
      {
        data: {
          InhoudelijkeOverweging: content ?? null,
          Modified_By: userId,
          Modified_Date: new Date().toISOString(),
        },
        id: sectieId,
      },
      {
        onSuccess: () => {
          setButtonState('success');

          successTimeoutRef.current = setTimeout(() => {
            if (isMounted()) {
              setButtonState(undefined);
            }
          }, 3000);
        },
        onError: () => {
          setButtonState(undefined);

          openModal(SaveFailed, {
            tryAgain: () =>
              patchStdMapId.mutateAsync({
                data: {
                  InhoudelijkeOverweging: content ?? null,
                  Modified_By: userId,
                  Modified_Date: new Date().toISOString(),
                },
                id: sectieId,
              }),
          });
        },
      }
    );
  };

  const warnings: string[] = [];

  if (map.data?.Status === 'Archief') {
    warnings.push(
      `Let op! De bovenliggende map “${map.data.Naam}” inclusief alle inhoud is onzichtbaar voor de gebruiker.`
    );
  }

  if (sectie.data?.Status === 'Archief') {
    warnings.push(
      `Let op! De bovenliggende sectie “${sectie.data.Naam}” inclusief alle inhoud is onzichtbaar voor de gebruiker.`
    );
  }

  return (
    <VoorschriftenLayout
      grondslagId={grondslagId}
      mapId={mapId}
      backLabel="Terug naar overzicht"
      testId={Route.fullPath}
      warnings={warnings}
      ref={containerRef}
    >
      <h1 className="style-h5-bold mb-2 text-gray-900">
        {sectie.data?.Naam} (Inhoudelijke overwegingen)
      </h1>

      <Divider className="my-3" />

      <TextEditor
        id="inhoudelijke-overwegingen"
        onUpdate={handleUpdate}
        editable
        menuBarClassName="sticky top-0"
        minHeight={containerHeight - 300}
        placeholder="Voeg hier de inhoudelijke overwegingen toe"
        initialText={sectie.data.InhoudelijkeOverweging ?? ''}
        menuOptions={[
          'bold',
          'italic',
          'orderedList',
          'abcList',
          'bulletList',
          'subscript',
          'superscript',
          'underline',
          'table',
          'highlight',
        ]}
      />

      <div className="fixed bottom-4 right-4 flex gap-1 rounded-md bg-white p-1 shadow-md">
        <Button variant="secondary" asChild>
          <Link
            from={Route.id}
            to="../.."
            activeOptions={{ exact: true }}
            search={{ p: 1 }}
          >
            Annuleren
          </Link>
        </Button>
        <Button variant="primary" onClick={handleSubmit} state={buttonState}>
          {buttonState === 'success' ? 'Opgeslagen' : 'Opslaan'}
        </Button>
      </div>
    </VoorschriftenLayout>
  );
}
